/*
 * Control marks: the tick, the dash, the chevron, the cross, the lens.
 *
 * These are drawn here rather than taken from the icon set because they are
 * geometry belonging to a control rather than iconography. A checkbox tick has
 * to grow with the box it sits inside and land on its optical centre, and a
 * select chevron has to match the field's own hairline; the icon set is sized
 * for the 20 to 24 pixel range and pulling a 24 pixel glyph into a 16 pixel box
 * produces a stroke that is visibly heavier than the border beside it.
 *
 * They still follow the icon rules exactly: a 1.6 unit hairline on a 24 unit
 * grid, round caps, no fills, so the two families cannot drift apart. And they
 * are all presentational. The accessible name always lives on the control that
 * contains the mark, never on the mark.
 */

interface MarkProps {
  size?: number
  className?: string
}

function base(size: number) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
  }
}

/** The checkbox tick. Drawn short, so it clears the box at 16 pixels. */
export function CheckMark({ size = 16, className }: MarkProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  )
}

/**
 * The indeterminate mark.
 *
 * A dash rather than a partly-filled tick: "some of the rows below are
 * selected" is a different statement from "this is selected", and a mark that
 * looks like a faint tick invites the reading that it is a tick that failed to
 * render.
 */
export function DashMark({ size = 16, className }: MarkProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M6 12h12" />
    </svg>
  )
}

/** The select chevron. */
export function ChevronMark({ size = 16, className }: MarkProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M6 9.5l6 6 6-6" />
    </svg>
  )
}

/** Dismiss: the clear button on a search field, the remove button on a tag. */
export function CrossMark({ size = 16, className }: MarkProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </svg>
  )
}

/** The lens that sits inside a search field. */
export function SearchMark({ size = 16, className }: MarkProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="11" cy="11" r="6" />
      <path d="M15.5 15.5L20 20" />
    </svg>
  )
}
